import React, { useState, useEffect } from 'react';

const Solutions = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const fullText = "Farmnet delivers on-demand services—putting expert advice, inputs, and market connections directly in farmers' hands when they need them most.";

  useEffect(() => {
    let currentIndex = 0;
    let typingInterval;

    const typeText = () => {
      if (currentIndex < fullText.length) {
        setText(fullText.substring(0, currentIndex + 1));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        setTimeout(() => {
          setIsTyping(false);
          setTimeout(() => {
            backspaceText();
          }, 2000);
        }, 1000);
      }
    };

    const backspaceText = () => {
      let backspaceInterval;
      let backspaceIndex = fullText.length;

      const eraseText = () => {
        if (backspaceIndex >= 0) {
          setText(fullText.substring(0, backspaceIndex));
          backspaceIndex--;
        } else {
          clearInterval(backspaceInterval);
          setIsTyping(true);
          currentIndex = 0;
          typingInterval = setInterval(typeText, 50);
        }
      };

      backspaceInterval = setInterval(eraseText, 20);
    };

    if (isTyping) {
      typingInterval = setInterval(typeText, 50);
    }

    return () => {
      clearInterval(typingInterval);
    };
  }, [isTyping, fullText]);

  return (
    <div className="relative bg-[#F3F3F3]">
      {/* Content */}
      <div className="relative z-10 px-4 md:px-24 pb-10 md:pt-5 pt-1">
        {/* Heading */}
        <div className="mt-2 md:mt-2 md:mb-5 flex justify-between items-center p mb-2">
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 ">
          <span className='text-[#152C24]'>INSIGHTS & INNOVATIONS</span>
          </h1>
          <div>
            <div className='flex md:gap-2 gap-1 justify-center md:mt-2'>
              <div className='bg-[#CFD7D7] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751045210/Expand_left_light_scmuqw.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
              <div className="bg-[#2E453D] border-b-2 border-[#FFBD26] md:ml-0 ml-2 text-white font-normal md:px-6 px-2 rounded-md md:rounded-[30px] w-[70px] h-[15px] md:w-[150px] md:text-[20px] text-[6px] md:h-[40px] mt-0 md:-mt-[1px] flex justify-between items-center">
                See More
              </div>
              <div className='bg-[#2E453D] w-[15px] h-[15px] md:w-[40px] md:h-[40px] flex justify-center md:mt-0 mr-2 rounded-full'> 
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="md:w-6 md:h-6 w-3 h-3 mt-2 md:flex hidden"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748783006/Expand_right_gzn0vr.png"
                  alt="Arrow"
                  className="w-2 h-2 mt-1 md:hidden flex"
                /> 
              </div>
            </div>
          </div>
        </div>

        {/* Records Section */}
        <div className="rounded-md w-full md:h-[630px] h-[175px] relative flex items-center justify-center p-2 md:p-0 pt-2 md:mt-4">
          <div className="rounded-xl overflow-hidden relative w-full h-[180px] md:mt-5 md:h-[700px] flex justify-center items-center cursor-pointer">
              <>
                <img
                  src="https://res.cloudinary.com/dwfdzdaxk/image/upload/v1751380763/Rectangle_34641496_yygcso.png"
                  alt="Mobile"
                  className="object-cover w-full h-full"
                />
              </>
          </div>
        </div>

        {/* Description */}
        <div className='md:text-[32px] text-[10px] font-normal md:mt-16 mt-2 space-y-6'>
          <h1 className="text-[12px] md:text-[36px] font-bold md:mb-3 text-[#979797]">
            INSIGHTS & INNOVATIONS | <span className='text-[#152C24]'>On-Demand Digital Services</span>
          </h1>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            📲 What Are On-Demand Digital Services in Agriculture?
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            On-demand digital services are digital tools and platforms that allow farmers to access agricultural products, services, or information exactly when they need them—often through a mobile phone or internet connection.
          </p>
          
          <p className="md:text-[32px] text-[8px]">
            These services are "on-demand" because they are:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Instant or near-instant</li>
            <li>Farmer-driven (accessed when the farmer decides)</li>
            <li>Tailored to specific needs (location, crop, livestock, time of year)</li>
          </ul>
          
          <p className="md:text-[32px] text-[8px]">
            They help farmers save time, reduce costs, and make better decisions by connecting them directly to resources without needing to travel or wait for physical visits.
          </p>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            🌍 How Smallholder Farmers in Africa Use On-Demand Digital Services
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            Smallholder farmers across Africa are increasingly using on-demand digital services for agronomic advice, inputs, mechanization, market access, and more. Here's how:
          </p>
          
          <ol className="md:text-[32px] text-[8px] list-decimal pl-5 space-y-3 [&>li::marker]:font-bold">
            <li>
              <span className="font-bold">Agronomic Advisory</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Farmers use mobile apps to ask questions and get instant crop or livestock advice</li>
                <li>Access planting guides, pest/disease solutions, and fertilizer tips</li>
                <li>Receive tailored alerts based on crop type and location</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Input Ordering & Delivery</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Order seeds, fertilizers, veterinary drugs, and tools via apps or USSD</li>
                <li>Have them delivered to the farm or nearby pick-up point</li>
                <li>Compare prices and ensure quality</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Mechanization as a Service</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Request tractors, planters, or harvesters on-demand</li>
                <li>Schedule services by phone or app and pay per use</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Market Access</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Find real-time market prices and post harvests for sale</li>
                <li>Connect directly to buyers</li>
                <li>Reduces post-harvest losses and increases earnings</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Weather & Climate Services</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Receive real-time weather updates and seasonal forecasts</li>
                <li>Improves planning for planting, irrigation, and harvesting</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Financial & Insurance Services</span>
              <ul className="list-disc pl-5 mt-1">
                <li>On-demand access to mobile loans and credit scoring</li>
                <li>Crop/livestock insurance via mobile</li>
                <li>Savings wallets for inputs or emergencies</li>
              </ul>
            </li>
            <li>
              <span className="font-bold">Veterinary and Extension Support</span>
              <ul className="list-disc pl-5 mt-1">
                <li>Call, text, or video chat with vets or extension officers</li>
                <li>Get immediate help for livestock health issues</li>
              </ul>
            </li>
          </ol>
          
          <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
            📱 How Farmnet Supports On-Demand Digital Services
          </h2>
          
          <p className="md:text-[32px] text-[8px]">
            The Farmnet App acts as a one-stop platform where farmers can:
          </p>
          
          <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
            <li>Access expert advice on crops or livestock instantly</li>
            <li>Request and order inputs or services through trusted partners</li>
            <li>Connect to digital extension officers for support</li>
            <li>Receive real-time updates on markets, weather, and pests</li>
            <li>Request market linkages when ready to sell</li>
          </ul>
          
          <p className="md:text-[32px] text-[8px]">
            Farmnet puts critical services in the farmer's pocket—available when and where they're needed most.
          </p>
          
          <div>
            <h2 className="text-[12px] md:text-[36px] font-bold text-[#152C24]">
              ✅ Benefits for Smallholder Farmers
            </h2>
            
            <ul className="md:text-[32px] text-[8px] list-disc pl-5 space-y-2">
              <li><span className="font-bold">Convenience:</span> No need to travel long distances for help or inputs</li>
              <li><span className="font-bold">Time savings:</span> Instant access to support</li>
              <li><span className="font-bold">Informed decisions:</span> Based on real-time data</li>
              <li><span className="font-bold">Lower costs:</span> By reducing middlemen and inefficiencies</li>
              <li><span className="font-bold">Greater resilience:</span> Especially during climate stress or disease outbreaks</li>
            </ul>
          </div>
        </div>
        
        <div className='flex justify-end items-center'>
          <div>
            <h1 className="text-[18px] md:text-[32px] font-bold mt-2 mb-4 text-black">
              Download App
            </h1>
            <div className="md:w-[450px]">
              <div className="flex flex-row justify-between space-x-2 items-center sm:space-y-0">
                <a href="https://play.google.com/store/apps/details?id=com.farmnet.africa&pcampaignid=web_share" alt="none">   
             <img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1751889690/google_PLAY-LOGO.WINE_emyxpu.png" className="h-[25px] md:w-[280px] md:h-[180px]" alt="App Store" />    
             <p className="md:text-[14px] text-[6px] md:-mt-12 md:pl-5 font-normal md:px-1">
             Click to download App
  </p>
     </a>
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744414874/image_scynki.png"
                  alt="Google Play"
                  className="h-[30px] md:h-[75px] object-contain"
                />
              </div>
            </div>
          </div>
        </div>
        <div className='md:h-10 h-5'></div>
      </div>
    </div>
  );
};

export default Solutions;